/**
 * 後台 — 推薦碼 / 優惠碼 / 顧問碼 管理(#推薦碼)
 *
 * 列表(可按類型篩)+ 建 campaign / consultant 碼 + 啟用/停用。
 * 會員推薦碼(type=member)由系統自動產,這裡唯讀列出。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminCreateCode, adminListCodes, adminToggleCode, type Code } from "../../api/admin";
import AdminHeader from "../../components/AdminHeader";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";


const TYPE_LABEL: Record<string, string> = {
  member: "會員推薦",
  campaign: "活動折扣",
  consultant: "顧問",
};


export default function CodeManage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);
  const [codes, setCodes] = useState<Code[]>([]);
  const [filterType, setFilterType] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // 建碼表單
  const [ctype, setCtype] = useState<"campaign" | "consultant">("campaign");
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [discountType, setDiscountType] = useState<"amount" | "percent">("amount");
  const [discountValue, setDiscountValue] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [usageLimit, setUsageLimit] = useState("");
  const [refereeBottles, setRefereeBottles] = useState("");
  const [referrerBottles, setReferrerBottles] = useState("");
  const [ownerUid, setOwnerUid] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, role]);

  async function refresh() {
    setLoading(true);
    try {
      const r = await adminListCodes(filterType || undefined);
      setCodes(r.codes);
    } catch {
      showToast("載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }

  async function create() {
    if (!label.trim()) {
      showToast(ctype === "consultant" ? "請填顧問名字" : "請填活動名稱", "error");
      return;
    }
    if (ctype === "campaign" && (!discountValue || Number(discountValue) <= 0)) {
      showToast("請填折扣值", "error");
      return;
    }
    setBusy(true);
    try {
      const r = await adminCreateCode({
        type: ctype,
        code: code.trim() || undefined,
        label: label.trim(),
        ...(ctype === "campaign"
          ? {
              discount_type: discountType,
              discount_value: Number(discountValue),
              valid_from: validFrom || undefined,
              valid_to: validTo || undefined,
              usage_limit: usageLimit ? Number(usageLimit) : 0,
            }
          : {
              // 顧問碼 — 折扣 / 瓶數都選填
              ...(discountValue && Number(discountValue) > 0
                ? { discount_type: discountType, discount_value: Number(discountValue) }
                : {}),
              referee_bottles: refereeBottles ? Number(refereeBottles) : 0,
              referrer_bottles: referrerBottles ? Number(referrerBottles) : 0,
              owner_uid: ownerUid.trim() || undefined,
            }),
      });
      if (r.status === "ok") {
        showToast(`已建立:${r.code}`, "success");
        setShowCreate(false);
        setCode("");
        setLabel("");
        setDiscountValue("");
        setValidFrom("");
        setValidTo("");
        setUsageLimit("");
        setRefereeBottles("");
        setReferrerBottles("");
        setOwnerUid("");
        refresh();
      } else if (r.status === "code_taken") {
        showToast("此碼已存在", "error");
      } else if (r.status === "invalid_code") {
        showToast(r.hint || "碼格式不符(限 2–20 碼英數 / - / _)", "error");
      } else {
        showToast(`建立未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setBusy(false);
    }
  }

  async function toggle(c: Code) {
    try {
      await adminToggleCode(c.code, !c.active);
      refresh();
    } catch {
      showToast("操作失敗", "error");
    }
  }

  function discountText(c: Code): string {
    const parts: string[] = [];
    if (c.discount_type === "percent") parts.push(`折 ${c.discount_value}%`);
    else if (c.discount_type === "amount" && c.discount_value) parts.push(`折 NT$ ${c.discount_value.toLocaleString()}`);
    if (c.type === "consultant") {
      if (c.referee_bottles) parts.push(`客 +${c.referee_bottles}瓶`);
      if (c.referrer_bottles) parts.push(`顧 +${c.referrer_bottles}瓶`);
    }
    return parts.length ? parts.join(" · ") : "—";
  }

  return (
    <div className="min-h-screen bg-cream">
      <AdminHeader active="codes" />
      <div className="p-6 max-w-4xl">
        <div className="card mb-5 flex flex-wrap gap-3 items-end">
          <div>
            <label className="label">類型</label>
            <select className="input min-w-32" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="">全部</option>
              <option value="member">會員推薦</option>
              <option value="campaign">活動折扣</option>
              <option value="consultant">顧問</option>
            </select>
          </div>
          <div className="ml-auto">
            <button onClick={() => setShowCreate((s) => !s)} className="btn-primary">
              {showCreate ? "收合" : "＋ 新增碼"}
            </button>
          </div>
        </div>

        {showCreate && (
          <div className="card mb-5 space-y-3">
            <div className="text-sm font-medium">
              新增 活動折扣 / 顧問 碼
              <span className="text-xs text-muted ml-2">(會員推薦碼已停用,推薦改用顧問碼)</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label">類型</label>
                <select className="input" value={ctype} onChange={(e) => setCtype(e.target.value as "campaign" | "consultant")}>
                  <option value="campaign">活動折扣碼</option>
                  <option value="consultant">顧問碼</option>
                </select>
              </div>
              <div>
                <label className="label">{ctype === "consultant" ? "顧問名字" : "活動名稱"}</label>
                <input
                  className="input"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={ctype === "consultant" ? "例:王顧問" : "例:週年慶"}
                />
              </div>
              <div>
                <label className="label">碼(留空自動產;2–20 碼英數 / - / _)</label>
                <input
                  className="input uppercase"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="自訂碼(如 WANG-2025)或留空"
                  maxLength={20}
                />
              </div>
              {/* 折扣 — 活動碼必填、顧問碼選填 */}
              {(ctype === "campaign" || ctype === "consultant") && (
                <div>
                  <label className="label">折扣{ctype === "consultant" ? "(選填)" : ""}</label>
                  <div className="flex gap-2">
                    <select
                      className="input flex-1"
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value as "amount" | "percent")}
                    >
                      <option value="amount">折定額 NT$</option>
                      <option value="percent">折 %</option>
                    </select>
                    <input
                      className="input w-28"
                      type="number"
                      min="0"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      placeholder={discountType === "percent" ? "%" : "金額"}
                    />
                  </div>
                </div>
              )}
              {ctype === "campaign" && (
                <>
                  <div>
                    <label className="label">總使用上限(0=無限)</label>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      value={usageLimit}
                      onChange={(e) => setUsageLimit(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="label">檔期起(選填)</label>
                    <input className="input" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
                  </div>
                  <div>
                    <label className="label">檔期迄(選填)</label>
                    <input className="input" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
                  </div>
                </>
              )}
              {ctype === "consultant" && (
                <>
                  <div>
                    <label className="label">客戶新客禮瓶數(選填)</label>
                    <input className="input" type="number" min="0" value={refereeBottles} onChange={(e) => setRefereeBottles(e.target.value)} placeholder="0" />
                  </div>
                  <div>
                    <label className="label">顧問獎勵瓶數(選填)</label>
                    <input className="input" type="number" min="0" value={referrerBottles} onChange={(e) => setReferrerBottles(e.target.value)} placeholder="0" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label">顧問會員 UID(選填,要送「顧問獎勵瓶數」才需綁)</label>
                    <input className="input" value={ownerUid} onChange={(e) => setOwnerUid(e.target.value)} placeholder="顧問的會員 UID(從會員列表複製)" />
                  </div>
                </>
              )}
            </div>
            <button onClick={create} className="btn-primary" disabled={busy}>
              {busy ? "建立中…" : "建立"}
            </button>
          </div>
        )}

        <div className="card">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted text-xs border-b border-line">
                <th className="text-left py-2">碼</th>
                <th className="text-left py-2">類型</th>
                <th className="text-left py-2">名稱 / 擁有者</th>
                <th className="text-left py-2">折扣</th>
                <th className="text-right py-2">已用</th>
                <th className="text-right py-2">狀態</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.code} className="border-b border-line/50">
                  <td className="py-2 font-mono">{c.code}</td>
                  <td className="py-2">{TYPE_LABEL[c.type] || c.type}</td>
                  <td className="py-2">{c.label || "—"}</td>
                  <td className="py-2">
                    {discountText(c)}
                    {c.type === "campaign" && c.usage_limit ? ` · 限 ${c.usage_limit}` : ""}
                  </td>
                  <td className="py-2 text-right">{c.used_count || 0}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => toggle(c)}
                      className={`text-xs px-2 py-1 rounded ${c.active ? "bg-sage text-white" : "bg-muted text-white"}`}
                    >
                      {c.active ? "啟用中" : "已停用"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {codes.length === 0 && (
            <div className="text-muted text-sm text-center py-6">{loading ? "載入中…" : "尚無碼"}</div>
          )}
          <div className="text-xs text-muted mt-3 text-right">共 {codes.length} 筆</div>
        </div>
      </div>
    </div>
  );
}
